/**
 * The `BlueprintContext` builder — the orchestrator's named job, finally written.
 *
 * ─── WHY THIS FILE DECIDES WHETHER ANY OF THIS WORKS ──────────────────────────────────────────────
 * In the functions repo: `buildSingleShotTextPrompt({ interactivity: !!context })`. The ENTIRE
 * interactivity layer — page state, data bindings, repeats, visibility, event intents — is gated on a
 * context being supplied. And `BlueprintContext` is documented as *"Everything the orchestrator sends
 * for interactivity"*, so the orchestrator is the named owner and had simply never sent any.
 *
 * Measured on `NTk1Nz`, same brief, same model, same sealed engine:
 *
 *   WITHOUT context   1 section tree · interactions: null · entites: [] · no page state
 *   WITH context      3 entities imported by real id · 3 state variables · 12 intents · coverage 3/3
 *
 * ─── WHAT IS MECHANICAL AND WHAT IS A JUDGEMENT CALL ──────────────────────────────────────────────
 * Almost all of it is mechanical: the entity graph already carries ids, types, descriptions and
 * `referenceEntityName`, so the page's data model is a graph walk. The one judgement call is which
 * entity a page is ABOUT, and that comes from the caller — a wrong `primaryEntity` poisons a whole page
 * and no amount of downstream cleverness recovers it.
 */
import { ContextEntity, ContextGlobal, ContextPage, BlueprintContext } from './blueprint.types';

/**
 * Hidden in every picker, and naming one in a generated page is a defect rather than a nicety.
 * Same set the editor enforces — see the system-fields rule.
 */
const SYSTEM_FIELDS = new Set(['id', 'id_auto_increment', 'parent_id']);

/** An entity document as it exists on the client. Only the parts this builder reads. */
export interface ClientEntity {
  id: string;
  name: string;
  description?: string;
  /** The field array. NOT `fields` — the stored key is `data`, and getting it wrong yields 0 fields. */
  data?: {
    name: string; type: string; description?: string;
    referenceEntityName?: string; extendedAttributes?: boolean;
  }[];
  /** `SYNCED` · `LOCAL` · `NEW`. LOCAL has no table and can never be queried. */
  status?: string;
}

export interface PageBrief {
  /** The page id Stage 0 minted. */
  pageId: string;
  /** The human name. */
  pageName: string;
  /** The description the user wrote. */
  brief: string;
  /** Which entity the page is about. The one judgement call — see the note above. */
  primaryEntity?: string;
  /** `list` · `detail` · `form` · `dashboard` · … Drives the runtime-state checklist. */
  archetype?: string;
  /** One line for the nav registry, so another page's intent can say why it would navigate here. */
  purpose?: string;
  /** Extra entities this page needs beyond the primary and its one hop (a lookup, a cross-reference). */
  alsoNeeds?: string[];
}

/**
 * The app's conventions, sent on every page.
 *
 * ⚠ **THIS TEXT IS A CONTRACT, NOT PROSE.** It shapes the intents Blueprint writes, and
 * `condition-compiler.ts` then parses those intents to build the conditional functions. The phrasing
 * dictated here IS the grammar that compiler accepts. Change a line and the compiler starts refusing
 * intents it used to understand — so the two are edited together, and the compiler's test fixtures are
 * taken from real Blueprint output rather than invented.
 *
 * The three visibility forms below were produced VERBATIM by two separate probes on two unrelated
 * entities, which is the evidence that stating them works.
 *
 * ═══ HOW TO ACTIVATE THE CONDITIONAL ROUTE — the one line, and why it belongs here ════════════════
 *
 * MEASURED 2026-08-14: 25 visibility intents across 9 live pages, and `compileCondition` compiled all
 * 25. Zero refusals. That was not luck — this file dictated four phrasings and that compiler accepted
 * exactly those four, so the loop was closed by construction and the conditional route was STARVED
 * rather than idle: a per-row condition was not refused, it was UNASKABLE.
 *
 * ⚠ OPENED 2026-08-17, and this note is the record of the trade. The policy now says the three
 * phrasings are a SHORTCUT, not the limit, and tells Blueprint to write any other rule in plain
 * language. The grammar is unchanged and stays exact-match — a fast path for the three states every
 * loading page has, saving a model call on each. Everything else falls through to the conditional lane,
 * where Forge writes the body with the full vocabulary.
 *
 * THE COST IS REAL AND IS THE POINT. Each out-of-grammar condition is one Atlas selection plus one
 * Forge call. `physicalParameters` carried 17 conditionals on one run; if most of those stop being
 * one of the three phrasings, that page gains most of 17 model calls. That is the price of conditions
 * that say what they mean instead of being contorted into four forms or dropped as `inert`.
 *
 * DO NOT close the loop again by teaching `compileCondition` the new forms. The refusal IS the routing:
 * an intent the grammar cannot compile leaves a described shell, and that shell is what the conditional
 * route generates. Widening the grammar silences the route.
 *
 * Since 2026-08-15 the four-form grammar is a FAST PATH, not a gate. Anything it cannot compile is
 * created as a described shell and wired immediately — Forge fills the body through the conditional
 * lane. The grammar saves a model call on trivial conditions; it no longer blocks anything.
 */
export const BEHAVIOUR_POLICY = [
  'State names are camelCase and describe the value, never the widget: @farmerList, @isLoading, @searchText.',
  'A list page loads its data on page load into a list state named <entity>List.',
  '',
  'Every page that loads data declares exactly these three runtime states, phrased exactly like this:',
  '  · a loading flag  @isLoading          — visibility intent: "@isLoading is true"',
  '  · an empty state  on the list         — visibility intent: "@<list> is empty"',
  '  · the populated view on the list      — visibility intent: "@<list> is not empty"',
  'Set @isLoading to true before a query and false after it, in the same function.',
  '',
  '── ANY OTHER VISIBILITY RULE: SAY WHAT YOU MEAN ──',
  'Those three phrasings are a shortcut for the three states every loading page has. They are NOT the',
  'limit of what a condition may say. For every other rule, write the condition in plain language and',
  'describe it exactly — do not bend it into "@x is true" and do not drop it.',
  'Anything that is not one of the three phrasings above is written by the plan generator, which has the',
  'full vocabulary: comparisons, arithmetic, and / or, nested logic, and per-row field access.',
  'Examples of conditions you SHOULD write when the page calls for them:',
  '  · "row.status is Active"                      — a per-row field comparison',
  '  · "@deadFishCount is greater than 0"          — a comparison to a value',
  '  · "@isEditing is true and @record is not empty" — compound',
  '  · "row.stockingDate is before @cutoffDate"    — a comparison between two values',
  'State every variable such a condition reads in its `states` list, exactly as for any other intent.',
  '',
  '',
  '── AN INPUT\'S VALUE MAY BE DESCRIBED, NOT ONLY REFERENCED ──',
  'Most input values are a state: `text="@farmerName"`. When the value is not a state but something',
  'COMPUTED from one — a joined name, a count, a label that depends on a flag, a button disabled while',
  'saving — describe it in plain language exactly as you would a visibility rule. Do NOT bend it into a',
  'bare @state that does not exist, and do NOT drop the control.',
  'Examples you SHOULD write when the page calls for them:',
  '  · disabled="@isSaving is true"                   — a button that greys out while saving',
  '  · disabled="@isSaving is true or @name is empty"  — compound',
  '  · text="@firstName and @lastName, separated by a space"',
  '  · text="how many rows @leadList holds"',
  'State every variable such a value reads in its `states` list, exactly as for any other intent.',
  'A computed value is written by the plan generator, which has the full vocabulary; you name the',
  'states and describe the result, nothing more.',
  '',
  'Navigation targets are page ids from the registry and nothing else. If a page you want is not in the',
  'registry, do not invent it — leave the control out of the design.',
  '',
  'A page that shows ONE record gets it in one of exactly two ways, and the choice decides what states',
  'it declares:',
  '  · HANDED IN — another page selected it. Declare only the global @selected<Entity> and READ it.',
  '    Do not query. This is the normal case for a detail page reached by tapping a row.',
  '  · FETCHED — the page looks it up itself. A query always returns MANY, so it needs a list to land',
  '    in first: declare an EntityList <entity>List AND the Entity that holds the extracted record,',
  '    and phrase the load intent as "query <entity> into @<entity>List, then set @<record> from',
  '    @<entity>List at 0".',
  'Never ask a query to write straight into an Entity state. A single-record variable cannot receive a',
  'query result, and a load function written that way cannot be built at all.',
  '',
  'When a row in a repeated list can be tapped to open another page:',
  '  · the list page declares a number state named <list>Index — it holds the tapped row index',
  '  · the record itself is handed on in a GLOBAL state named selected<Entity> (e.g. @selectedFarmer)',
  '  · the row\'s tap intent is phrased: "Set @selected<Entity> from @<list> at @<list>Index,',
  '    then navigate to <pageId>."',
  '  · the destination page reads @selected<Entity>; it does NOT query for the record again',
  'Name the state and the intent only. SET_ENTITY_FROM_LIST is not named here — that is the plan',
  'generator\'s vocabulary, not yours.',
  '',
  'A row in a repeated list refers to its record as row.<field>, following references with dots.',
  '',
  '── GLOBALS AS CONTEXT ──',
  'Each global variable listed in the context holds one FULL record of its entity, with all its fields.',
  'Any page may READ them directly as @global.field for display or logic.',
  'Do NOT re-query an entity whose record is already in a global.',
  '',
  'When a page needs child records scoped to a global, query using the global as a foreign-key filter.',
  'Phrase the load intent as: "query <childEntity> where <fkField> matches @<global>, then …".',
  'The global holds the parent record — it is the filter, not the target.',
  '',
  'When a form page saves a record that belongs to a parent captured in a global, link it on save.',
  'Phrase the save intent as: "create <entity> with <fkField> from @<global>, …" or',
  '"update <entity> with <fkField> from @<global>, …". The foreign key comes from the global,',
  'not from a separate query.',
  '',
  'When a page creates a new record that downstream pages need, store it in a global immediately.',
  'Phrase the intent as: "create <entity>, then set @<global> from the result." The global is',
  'scope="global" so every subsequent page in the flow can read it.',
  '',
  'A value derived from globals or from globals combined with query results is a COMPUTED state.',
  'Declare a standalone number or text state for it and compute it in the load function or in a',
  'function triggered by a control change. Do not bind a computed value to an entity field.',
  '',
  'An edit/view toggle on a data card uses a boolean state (e.g. @isEditing). When false, fields',
  'display as read-only text. When true, fields become editable inputs. The pencil/edit button',
  'toggles @isEditing. Phrase visibility intents as "@isEditing is true" and "@isEditing is false".',
].join('\n');

/**
 * One entity → the shape Blueprint reads.
 *
 * `id` is not optional in practice. The type's own comment: a variable is stored as `{entityId,
 * entityName, …}` and without a real id it *"looks real, appears in every picker, and resolves to
 * nothing"* — so a page would be generated against an entity that cannot be queried, and every symptom
 * would appear at runtime rather than here.
 */
export function toContextEntity(e: ClientEntity, opts?: { lookup?: boolean; optionsFor?: string }): ContextEntity {
  const fields = (e.data ?? []).filter((f) => !SYSTEM_FIELDS.has(f.name));
  return {
    id: e.id,
    name: e.name,
    ...(e.description ? { description: e.description } : {}),
    ...(titleFieldOf(fields) ? { titleField: titleFieldOf(fields) } : {}),
    fields: fields.map((f) => ({
      name: f.name,
      type: f.type,
      ...(f.description ? { description: f.description } : {}),
      ...(f.type === 'ENTITY_REFERENCE' && f.referenceEntityName ? { ref: f.referenceEntityName } : {}),
    })),
    // LOCAL means no table: it can never be queried, its rows come from predefinedData. Blueprint has
    // to know, or it writes a query against something that will always return nothing.
    ...(e.status === 'LOCAL' ? { local: true } : {}),
    ...(opts?.lookup ? { lookup: true } : {}),
    ...(opts?.optionsFor ? { optionsFor: opts.optionsFor } : {}),
  };
}

/**
 * The field to show when one record is named — a list row, a reference, a dropdown option.
 *
 * Preference order is deliberate: an exact `name`, then something ending in `name`, then the first
 * plain string. Guessing wrong is cosmetic (a row labelled by the wrong column), which is why this is a
 * heuristic and not a model call.
 */
function titleFieldOf(fields: { name: string; type: string }[]): string | undefined {
  const strings = fields.filter((f) => f.type === 'STRING');
  return strings.find((f) => f.name.toLowerCase() === 'name')?.name
    ?? strings.find((f) => /name$/i.test(f.name))?.name
    ?? strings.find((f) => /(title|label|code)$/i.test(f.name))?.name
    ?? strings[0]?.name;
}

/**
 * The primary entity plus the FULL reference chain downward, plus anything the brief explicitly also needs.
 *
 * ── WHY TRANSITIVE, NOT ONE HOP (2026-08-18) ────────────────────────────────────────────────────────
 * The original one-hop walk was enough for Blueprint to see immediate neighbours, but it missed every
 * entity further down the chain. That mattered the moment globals entered the picture: a global holds
 * an entity, and the entity-scoped globals filter (§1b in atlas-grouped-view) drops any global whose
 * entity the page didn't import. One hop meant a page about `Farmer` never saw `salesExecutive` even
 * if the chain reached it through references — so Blueprint wrote descriptions using globals Atlas
 * could never see.
 *
 * The walk is cycle-safe: `picked` guards against revisiting an entity, so A→B→C→A terminates.
 * `alsoNeeds` entities are walked too — if the spec says the page needs entity X, X's children matter.
 */
export function entitiesForPage(
  all: ClientEntity[], primaryEntity: string, alsoNeeds: string[] = [],
): ContextEntity[] {
  const byName = new Map(all.map((e) => [e.name, e]));
  const byId = new Map(all.map((e) => [e.id, e]));
  const resolve = (key: string) => byName.get(key) ?? byId.get(key);

  const primary = resolve(primaryEntity);
  if (!primary) {
    // Loud. A missing primary is the one error nothing downstream recovers from, and silently sending a
    // context with no primary produces a plausible page about nothing.
    throw new Error(
      `entitiesForPage: no entity named "${primaryEntity}". A page must be ABOUT something that exists.`,
    );
  }

  const picked = new Map<string, ContextEntity>();

  /** Walk ENTITY_REFERENCE fields recursively. `picked` is the visited set — cycles terminate. */
  const walk = (e: ClientEntity) => {
    if (picked.has(e.id)) return;
    picked.set(e.id, toContextEntity(e));
    for (const f of e.data ?? []) {
      if (f.type !== 'ENTITY_REFERENCE' || !f.referenceEntityName) continue;
      const target = resolve(f.referenceEntityName);
      if (target) walk(target);
    }
  };
  walk(primary);

  for (const extra of alsoNeeds) {
    const e = resolve(extra);
    if (!e) {
      throw new Error(
        `entitiesForPage: alsoNeeds references "${extra}" but no entity with that name exists in the catalog. ` +
        `Fix the page spec — every name in alsoNeeds must resolve to a real entity.`,
      );
    }
    walk(e);
  }

  // LOCAL entities are never sent to Blueprint — the model creates <Struct> declarations
  // for any local data shapes it needs.  Sending them taught the model to "query" entities
  // that have no database table (Scar 8, 2026-08-30).
  return [...picked.values()].filter(e => !e.local);
}

/**
 * The page registry — the CLOSED set of navigation targets.
 *
 * All ten pages, built from Stage 0 before the first blueprint call. This is precisely why Stage 0
 * pre-creates every page up front: a nav target that does not exist yet is indistinguishable from one
 * that never will, and Blueprint is told to leave the control out rather than invent a destination.
 *
 * When `navTokens` is supplied (a pageId→$N_ map pre-seeded before the build), each page carries its
 * `$N_` token so Blueprint can write navigate intents that are already in $-space — no downstream
 * resolution needed.
 */
export function pageRegistry(briefs: PageBrief[], navTokens?: Map<string, string>): ContextPage[] {
  return briefs.map((b) => ({
    id: b.pageId,
    name: b.pageName,
    ...(navTokens?.get(b.pageId) ? { token: navTokens.get(b.pageId)! } : {}),
    ...(b.purpose ? { purpose: b.purpose } : {}),
    ...(b.primaryEntity ? { primaryEntity: b.primaryEntity } : {}),
  }));
}

/**
 * Shape the raw Firestore `globalVaraible` document into `ContextGlobal[]`.
 *
 * The document is a flat map: `{ selectedFarmer: { type, entityName, entityId, data }, … }`.
 * Only ENTITY-type globals are meaningful to Blueprint — scalar globals (if any existed) would
 * already be covered by the behaviour policy's camelCase naming rule.
 *
 * The fields come from the live entity the global holds. The stored value is empty until the editor fills it, and
 * can be an old copy from before a field was added or removed, so reading its keys told Blueprint a global had no
 * fields, or fields that no longer exist. A value the editor filled has exactly the live entity's fields minus the
 * system ones, so that line reads the same either way. Only a global whose entity is gone still reads its value.
 */
export function globalsForContext(raw: Record<string, any> | undefined, entities?: ClientEntity[]): ContextGlobal[] {
  if (!raw) return [];
  const byId = new Map((entities ?? []).map((e) => [e.id, e]));
  return Object.entries(raw)
    .filter(([k, v]) => k !== 'id' && k !== 'atlasMeta' && v?.type === 'ENTITY' && v.entityName)
    .map(([name, v]) => {
      const live = byId.get(v.entityId);
      return {
        name,
        entityName: v.entityName,
        fields: live
          ? (live.data ?? []).filter((f) => !SYSTEM_FIELDS.has(f.name)).map((f) => f.name)
          : v.data ? Object.keys(v.data).filter((f) => f !== 'id') : [],
      };
    });
}

/** Everything above, for one page of a run. Pure — no I/O, so it is testable without a client. */
export function buildBlueprintContext(
  entities: ClientEntity[], briefs: PageBrief[], forPage: PageBrief,
  globals?: Record<string, any>,
  /** The app's visual contract, authored once per project. Omitted → the block is simply absent. */
  designSystem?: string,
  /** Pre-seeded pageId→$N_ map. When present, Blueprint writes $N_ tokens in navigate intents. */
  navTokens?: Map<string, string>,
): BlueprintContext {
  const allGlobals = globalsForContext(globals, entities);

  // ── ENTITY-SCOPED GLOBALS (2026-08-18) ────────────────────────────────────────────────────────────
  // Blueprint and Atlas MUST see the same globals, and Atlas drops any global whose entity the page
  // does not import (atlas-grouped-view §1b: "droppedUnimported"). Without this filter, Blueprint
  // writes descriptions using @currentExecutive on a Farmer page, Atlas can't see the global because
  // salesExecutive isn't imported, and the function fails with "variable not defined".
  //
  // The fix: compute the page's entity set FIRST, then filter globals to those whose entity appears in
  // that set. A global holding an entity the page has no schema knowledge of is unusable anyway — the
  // page has no columns to filter by, no fields to display, and no reference linking the two.
  let pageEntities: ContextEntity[] = [];
  if (forPage.primaryEntity) {
    pageEntities = entitiesForPage(entities, forPage.primaryEntity, forPage.alsoNeeds);
  }

  const entityNames = new Set(pageEntities.map((e) => e.name));
  const shaped = allGlobals.filter((g) => entityNames.has(g.entityName));

  return {
    behaviourPolicy: BEHAVIOUR_POLICY,
    ...(designSystem && designSystem.trim() ? { designSystem: designSystem.trim() } : {}),
    pages: pageRegistry(briefs, navTokens),
    ...(shaped.length ? { globals: shaped } : {}),
    ...(pageEntities.length
      ? { entities: pageEntities, primaryEntity: forPage.primaryEntity }
      // A page about nothing — a landing page, a static help page — is legitimate. It gets the policy
      // and the nav registry so its buttons can still navigate, and no data model at all.
      : {}),
    ...(forPage.archetype ? { archetype: forPage.archetype } : {}),
    // ── EVERY entity, unscoped — Prism's input and only Prism's ──────────────────────────────────
    // `entities` above is the PAGE's closure, walked OUTWARD from the page's primary. A chart needs
    // the closure of ITS OWN entity in BOTH directions, so the page's cannot serve it: different
    // root, one direction. `prismContext(chartEntity, catalogue)` scopes per chart from this.
    //
    // ⚠ Set HERE rather than at each call site, because there are two — the Angular service and the
    // Drydock harness — and a harness that silently fell back to `entities` would produce a
    // comparison run that appeared to test the schema block while testing almost none of it.
    //
    // Costs HTTP payload, not tokens: the model only ever sees the scoped subset. Measured on
    // NzkyMj, 11 entities of 48 for speciesWeightDetails and 6 for Order.
    ...(entities.length ? { catalogue: entities.map((e) => toContextEntity(e)) } : {}),
  };
}
